import React from 'react';
import PropTypes from 'prop-types';

import { cn } from 'helpers/classnames';
import MartyLink from 'components/common/MartyLink';
import { pluralize } from 'helpers';
import { isOrderCancellable, isOrderReturnable } from 'helpers/MyAccountUtils';
import useMartyContext from 'hooks/useMartyContext';
import { RETURN_INITIATE_SOURCE } from 'constants/amethystEnums';
import { toFloatInt } from 'helpers/NumberFormats';

import css from 'styles/components/account/orderSummary.scss';

const OrderSummary = ({ order, keepExchangeOrdersFromReturnCancel, showBatchReturn }) => {
  const { lineItems, pricing } = order;
  const { marketplace, testId } = useMartyContext();
  const {
    dataMaskPiiElements,
    account: { crossOrderReturns },
    returns: { details, moreInfoUrl }
  } = marketplace;

  return (
    <div className={css.orderSummary} data-test-id="orderSummaryContainer">
      <div className={css.orderActions}>
        {isOrderReturnable(lineItems, keepExchangeOrdersFromReturnCancel) && (
          <>
            {crossOrderReturns && showBatchReturn && (
              <MartyLink
                className={css.addAdditional}
                to="/returns"
                data-test-id={testId('returnAllItemsLink')}
                data-amethyst-return-behavior-survey={RETURN_INITIATE_SOURCE.ORDER_PAGE_ADD_ADDITIONAL_ITEMS_BUTTON}
              >
                Add Additional Items To Your Return
              </MartyLink>
            )}
          </>
        )}
        <div className={css.shippingNote}>
          <div className={cn(css.shippingNoteDetails, 'zappos:body-s')}>{details}</div>
          <MartyLink className="zappos:!body-m-medium" to={moreInfoUrl}>
            Learn More About Return Shipping
          </MartyLink>
        </div>
      </div>

      <div
        className={cn(css.priceSummary, {
          [css.hasSelectionColumn]:
            isOrderReturnable(lineItems, keepExchangeOrdersFromReturnCancel) || isOrderCancellable(lineItems, keepExchangeOrdersFromReturnCancel)
        })}
      >
        <h2 className={cn(css.summaryHeading, 'zappos:heading-l')}>Order Summary</h2>
        <dl className="zappos:body-s" data-test-id={testId('orderSummary')} data-cs-mask={dataMaskPiiElements}>
          {/* 1) Subtotal */}
          <div>
            <dt data-test-id={testId('subtotalString')}>
              Subtotal ({lineItems.length} {pluralize('item', lineItems.length)}):
            </dt>
            <dd className={css.definition} data-test-id={testId('subtotal')}>
              {pricing.subtotal}
            </dd>
          </div>
          {/* 2) Discount(s) */}
          {pricing.pretax.map(({ description, amount }, index) => (
            <div key={index}>
              <dt data-test-id={testId('pretaxDesc')}>{description}:</dt>
              <dd className={css.definition} data-test-id={testId('pretaxDiscount')}>
                {amount}
              </dd>
            </div>
          ))}
          {/* 3) Shipping */}
          <div>
            <dt>Shipping:</dt>
            <dd className={css.definition} data-test-id={testId('shippingCost')}>
              {pricing.shipping === '$0.00' ? 'FREE' : pricing.shipping}
            </dd>
          </div>
          {/* 4) Shipping Discounts */}
          {pricing.shippingDiscounts.map(({ description, amount }, index) => (
            <div key={index}>
              <dt data-test-id={testId('shippingDiscountDesc')}>{description}:</dt>
              <dd className={css.definition} data-test-id={testId('shippingDiscount')}>
                {amount}
              </dd>
            </div>
          ))}
          {/* 5) Tax */}
          <div>
            <dt>Collected Tax:</dt>
            <dd className={css.definition} data-test-id={testId('tax')}>
              {pricing.tax}
            </dd>
          </div>
          {/* 6) Tax */}
          {pricing.retailDeliveryFee && toFloatInt(pricing.retailDeliveryFee) !== 0 && (
            <div>
              <dt>CO Retail Delivery Fees:</dt>
              <dd className={css.definition} data-test-id={testId('deliveryFee')}>
                {pricing.retailDeliveryFee}
              </dd>
            </div>
          )}

          {/* 7) Voucher */}
          {pricing.posttax.map(({ description, amount }, index) => (
            <div key={index}>
              <dt data-test-id={testId('voucherDesc')}>{description}:</dt>
              <dd className={css.definition} data-test-id={testId('voucherAmount')}>
                {amount}
              </dd>
            </div>
          ))}
          {/* 8) Order Total */}
          <div>
            <dt className={css.total}>Order Total:</dt>
            <dd className={cn(css.definition, css.total)} data-test-id={testId('totalCost')}>
              {pricing.total}
            </dd>
          </div>
          {/* 9) Reversals (Credit) */}
          {pricing.reversals.map(({ description, amount }, index) => (
            <div key={index}>
              <dt data-test-id={testId('reversalDesc')}>{description}:</dt>
              <dd className={css.definition} data-test-id={testId('reversalAmount')}>
                {amount}
              </dd>
            </div>
          ))}
          {/* 10) Total Charged */}
          <div>
            <dt className={css.totalCharged}>Total Charged:</dt>
            <dd className={cn(css.definition, css.totalCharged)} data-test-id={testId('totalCharged')}>
              {pricing.totalCharged}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

OrderSummary.propTypes = {
  order: PropTypes.object.isRequired
};

export default OrderSummary;
